import React, { useState } from 'react';
import { CompanyRegion, VisibilityPreference } from '../data/auth';
import './Register.css';

interface CompanyRegistrationProps {
  onSuccess: () => void;
  onBack: () => void;
  referrerId?: string | null;
}

const CompanyRegistration: React.FC<CompanyRegistrationProps> = ({
  onSuccess,
  onBack,
  referrerId,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [region, setRegion] = useState<CompanyRegion>('south-africa');

  const [formData, setFormData] = useState({
    // Account credentials
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',

    // Basic company info
    visibilityPreference: 'public' as VisibilityPreference,
    companyName: '',
    industry: '',
    companySize: '',
    website: '',
    foundedYear: '',
    description: '',
    services: '',

    // South African specific
    cipcNumber: '',
    registrationNumber: '',
    vatNumber: '',
    bbbeeLevel: '',
    bbbeeVerificationCertificate: '',

    // Other African specific
    africaCountry: '',
    africaRegistrationBody: '',
    africaRegistrationNumber: '',

    // European specific
    europeanCountry: '',
    euVatNumber: '',
    companyHouseNumber: '',
    europeanRegistrationBody: '',

    // Contact person
    contactPerson: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',

    // Address
    streetAddress: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: 'South Africa',

    // Privacy
    showCompanyName: true,
    showContactDetails: true,
    showAddress: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRegion = e.target.value as CompanyRegion;
    setRegion(newRegion);

    // Update country based on region
    if (newRegion === 'south-africa') {
      setFormData({ ...formData, country: 'South Africa' });
    } else if (newRegion === 'europe') {
      setFormData({ ...formData, country: formData.europeanCountry || '' });
    } else if (newRegion === 'other-africa') {
      setFormData({ ...formData, country: formData.africaCountry || '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const profileData: any = {
        type: 'company',
        visibilityPreference: formData.visibilityPreference,
        region,
        companyName: formData.companyName,
        industry: formData.industry.split(',').map((i) => i.trim()),
        companySize: formData.companySize,
        website: formData.website,
        foundedYear: formData.foundedYear ? parseInt(formData.foundedYear) : undefined,
        description: formData.description,
        services: formData.services.split(',').map((s) => s.trim()),
        contactPerson: formData.contactPerson,
        contactTitle: formData.contactTitle,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        streetAddress: formData.streetAddress,
        city: formData.city,
        stateProvince: formData.stateProvince,
        postalCode: formData.postalCode,
        country: formData.country,
        showCompanyName: formData.showCompanyName,
        showContactDetails: formData.showContactDetails,
        showAddress: formData.showAddress,
      };

      // Add region-specific fields
      if (region === 'south-africa') {
        profileData.registrationNumber = formData.registrationNumber;
        profileData.cipcNumber = formData.cipcNumber;
        profileData.vatNumber = formData.vatNumber;
        profileData.bbbeeLevel = formData.bbbeeLevel;
        profileData.bbbeeVerificationCertificate = formData.bbbeeVerificationCertificate;
      } else if (region === 'other-africa') {
        profileData.africaCountry = formData.africaCountry;
        profileData.africaRegistrationBody = formData.africaRegistrationBody;
        profileData.registrationNumber = formData.africaRegistrationNumber;
      } else if (region === 'europe') {
        profileData.europeanCountry = formData.europeanCountry;
        profileData.euVatNumber = formData.euVatNumber;
        profileData.companyHouseNumber = formData.companyHouseNumber;
        profileData.registrationNumber = formData.companyHouseNumber || formData.euVatNumber;
      }

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'company',
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          referrerId: referrerId || undefined,
          profile: profileData,
        }),
      });

      const data = await response.json();

      if (data.ok) {
        alert('Registration successful! Please check your email to verify your account.');
        onSuccess();
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderRegionSpecificFields = () => {
    if (region === 'south-africa') {
      return (
        <>
          <h3>South African Company Information</h3>
          <div className="form-group">
            <label htmlFor="cipcNumber">CIPC Registration Number *</label>
            <input
              id="cipcNumber"
              name="cipcNumber"
              type="text"
              value={formData.cipcNumber}
              onChange={handleChange}
              placeholder="2015/123456/07"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="registrationNumber">Company Registration Number *</label>
            <input
              id="registrationNumber"
              name="registrationNumber"
              type="text"
              value={formData.registrationNumber}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="vatNumber">VAT Number</label>
            <input
              id="vatNumber"
              name="vatNumber"
              type="text"
              value={formData.vatNumber}
              onChange={handleChange}
              placeholder="4123456789"
            />
          </div>

          <div className="form-group">
            <label htmlFor="bbbeeLevel">B-BBEE Level</label>
            <select
              id="bbbeeLevel"
              name="bbbeeLevel"
              value={formData.bbbeeLevel}
              onChange={handleChange}
            >
              <option value="">Select B-BBEE Level</option>
              <option value="Level 1">Level 1</option>
              <option value="Level 2">Level 2</option>
              <option value="Level 3">Level 3</option>
              <option value="Level 4">Level 4</option>
              <option value="Level 5">Level 5</option>
              <option value="Level 6">Level 6</option>
              <option value="Level 7">Level 7</option>
              <option value="Level 8">Level 8</option>
              <option value="Non-compliant">Non-compliant</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="bbbeeVerificationCertificate">B-BBEE Certificate Number</label>
            <input
              id="bbbeeVerificationCertificate"
              name="bbbeeVerificationCertificate"
              type="text"
              value={formData.bbbeeVerificationCertificate}
              onChange={handleChange}
            />
          </div>
        </>
      );
    }

    if (region === 'other-africa') {
      return (
        <>
          <h3>African Company Information</h3>
          <div className="form-group">
            <label htmlFor="africaCountry">Country *</label>
            <select
              id="africaCountry"
              name="africaCountry"
              value={formData.africaCountry}
              onChange={(e) => {
                handleChange(e);
                setFormData({
                  ...formData,
                  country: e.target.value,
                  africaCountry: e.target.value,
                });
              }}
              required
            >
              <option value="">Select Country</option>
              <option value="Nigeria">Nigeria</option>
              <option value="Kenya">Kenya</option>
              <option value="Ghana">Ghana</option>
              <option value="Tanzania">Tanzania</option>
              <option value="Uganda">Uganda</option>
              <option value="Ethiopia">Ethiopia</option>
              <option value="Egypt">Egypt</option>
              <option value="Morocco">Morocco</option>
              <option value="Zimbabwe">Zimbabwe</option>
              <option value="Botswana">Botswana</option>
              <option value="Namibia">Namibia</option>
              <option value="Other">Other African Country</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="africaRegistrationBody">Registration Body *</label>
            <input
              id="africaRegistrationBody"
              name="africaRegistrationBody"
              type="text"
              value={formData.africaRegistrationBody}
              onChange={handleChange}
              placeholder="e.g., CAC (Nigeria), Registrar of Companies"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="africaRegistrationNumber">Registration Number *</label>
            <input
              id="africaRegistrationNumber"
              name="africaRegistrationNumber"
              type="text"
              value={formData.africaRegistrationNumber}
              onChange={handleChange}
              required
            />
          </div>
        </>
      );
    }

    if (region === 'europe') {
      return (
        <>
          <h3>European Company Information</h3>
          <div className="form-group">
            <label htmlFor="europeanCountry">Country *</label>
            <select
              id="europeanCountry"
              name="europeanCountry"
              value={formData.europeanCountry}
              onChange={(e) => {
                handleChange(e);
                setFormData({
                  ...formData,
                  country: e.target.value,
                  europeanCountry: e.target.value,
                });
              }}
              required
            >
              <option value="">Select Country</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="Germany">Germany</option>
              <option value="France">France</option>
              <option value="Netherlands">Netherlands</option>
              <option value="Belgium">Belgium</option>
              <option value="Switzerland">Switzerland</option>
              <option value="Spain">Spain</option>
              <option value="Italy">Italy</option>
              <option value="Sweden">Sweden</option>
              <option value="Denmark">Denmark</option>
              <option value="Ireland">Ireland</option>
              <option value="Other">Other European Country</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="companyHouseNumber">Company Registration Number *</label>
            <input
              id="companyHouseNumber"
              name="companyHouseNumber"
              type="text"
              value={formData.companyHouseNumber}
              onChange={handleChange}
              placeholder="e.g., Companies House Number (UK)"
              required
            />
            <small>Company House Number (UK), Handelsregister (DE), etc.</small>
          </div>

          <div className="form-group">
            <label htmlFor="euVatNumber">EU VAT Number</label>
            <input
              id="euVatNumber"
              name="euVatNumber"
              type="text"
              value={formData.euVatNumber}
              onChange={handleChange}
              placeholder="e.g., GB123456789"
            />
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="register-page">
      <div className="register-container large">
        <div className="register-header">
          <button onClick={onBack} className="back-button">
            ← Back
          </button>
          <h1>Company Registration</h1>
          <p>Step {step} of 4</p>
        </div>

        <div className="progress-bar">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>Account</div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>Company</div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>Contact</div>
          <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>Privacy</div>
        </div>

        <form onSubmit={handleSubmit} className="registration-form">
          {step === 1 && (
            <div className="form-step">
              <h2>Account Information</h2>

              <div className="form-group">
                <label htmlFor="email">Company Email Address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Company Phone Number *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+27117654321"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>

              <button type="button" onClick={() => setStep(2)} className="next-button">
                Next →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <h2>Company Information</h2>

              <div className="form-group">
                <label htmlFor="region">Company Region *</label>
                <select id="region" value={region} onChange={handleRegionChange} required>
                  <option value="south-africa">South Africa</option>
                  <option value="other-africa">Other African Country</option>
                  <option value="europe">Europe</option>
                  <option value="other">Other Region</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                />
              </div>

              {renderRegionSpecificFields()}

              <div className="form-group">
                <label htmlFor="industry">Industry/Sector (comma separated) *</label>
                <input
                  id="industry"
                  name="industry"
                  type="text"
                  value={formData.industry}
                  onChange={handleChange}
                  placeholder="Pharmaceuticals, Clinical Research, Medical Devices"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="companySize">Company Size *</label>
                <select
                  id="companySize"
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-100">51-100 employees</option>
                  <option value="101-500">101-500 employees</option>
                  <option value="501+">501+ employees</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="foundedYear">Year Founded</label>
                <input
                  id="foundedYear"
                  name="foundedYear"
                  type="number"
                  value={formData.foundedYear}
                  onChange={handleChange}
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>

              <div className="form-group">
                <label htmlFor="website">Company Website</label>
                <input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.yourcompany.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Company Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Brief description of your company and services..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="services">Services Offered (comma separated) *</label>
                <input
                  id="services"
                  name="services"
                  type="text"
                  value={formData.services}
                  onChange={handleChange}
                  placeholder="Site Management, Patient Recruitment, Data Management"
                  required
                />
              </div>

              <div className="button-row">
                <button type="button" onClick={() => setStep(1)} className="back-button-form">
                  ← Previous
                </button>
                <button type="button" onClick={() => setStep(3)} className="next-button">
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-step">
              <h2>Contact Information</h2>

              <div className="form-group">
                <label htmlFor="contactPerson">Contact Person Name *</label>
                <input
                  id="contactPerson"
                  name="contactPerson"
                  type="text"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactTitle">Contact Person Title *</label>
                <input
                  id="contactTitle"
                  name="contactTitle"
                  type="text"
                  value={formData.contactTitle}
                  onChange={handleChange}
                  placeholder="e.g., Clinical Operations Manager"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactEmail">Contact Email *</label>
                <input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactPhone">Contact Phone *</label>
                <input
                  id="contactPhone"
                  name="contactPhone"
                  type="tel"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  required
                />
              </div>

              <h3>Company Address</h3>

              <div className="form-group">
                <label htmlFor="streetAddress">Street Address *</label>
                <input
                  id="streetAddress"
                  name="streetAddress"
                  type="text"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="stateProvince">State/Province *</label>
                  <input
                    id="stateProvince"
                    name="stateProvince"
                    type="text"
                    value={formData.stateProvince}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode">Postal Code *</label>
                  <input
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    value={formData.postalCode}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="button-row">
                <button type="button" onClick={() => setStep(2)} className="back-button-form">
                  ← Previous
                </button>
                <button type="button" onClick={() => setStep(4)} className="next-button">
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="form-step">
              <h2>Privacy Settings</h2>

              <div className="form-group">
                <label htmlFor="visibilityPreference">Company Profile Visibility *</label>
                <select
                  id="visibilityPreference"
                  name="visibilityPreference"
                  value={formData.visibilityPreference}
                  onChange={handleChange}
                  required
                >
                  <option value="public">Public - Show full company profile</option>
                  <option value="anonymous">Anonymous - Show only services and expertise</option>
                </select>
                <small>
                  Anonymous profiles reveal company identity only after mutual agreement
                </small>
              </div>

              <div className="privacy-options">
                <h3>What information do you want to show publicly?</h3>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="showCompanyName"
                      checked={formData.showCompanyName}
                      onChange={handleChange}
                    />
                    Show company name
                  </label>
                </div>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="showContactDetails"
                      checked={formData.showContactDetails}
                      onChange={handleChange}
                    />
                    Show contact person details
                  </label>
                </div>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="showAddress"
                      checked={formData.showAddress}
                      onChange={handleChange}
                    />
                    Show physical address
                  </label>
                </div>
              </div>

              <div className="info-box">
                <strong>Note:</strong> Regardless of your privacy settings:
                <ul>
                  <li>Your services and expertise areas are always visible</li>
                  <li>Full company details are revealed after project acceptance</li>
                  <li>Verified company badge is always shown</li>
                </ul>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="button-row">
                <button type="button" onClick={() => setStep(3)} className="back-button-form">
                  ← Previous
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Complete Registration'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CompanyRegistration;
